export interface Brand {
  brandId: number;
  name: string;
  productCount: number;
}

// ─── Catalog category (from EBM /api/catalog/{companyId}/categories) ───────

export interface CatalogCategory {
  id: number;           // productSubCategoryId
  name: string;
  image: string | null; // S3 presigned URL
  productCount: number;
}

// ─── Hierarchical category types (from real API /api/catalog/{companyId}/categories) ─

export interface SubCategory {
  productSubCategoryId: number;
  name: string;
  image: string | null;
  productCount: number;
  productCategoryId: number;
  categoryName: string;
}

export interface CategoryGroup {
  productCategoryId: number;
  name: string;
  image: string | null;
  productCount: number;
  subCategories: SubCategory[];
}

// ─── InnoFlex API raw types ──────────────────────────────────────────────────────

export interface CatalogAvailability {
  stockId: number;
  branchId: number;
  branchName: string;
  warehouseId: number | null;
  warehouseName: string | null;
  quantity: number;
  price: number;
  stockLocation: string | null;
  primaryIdentifier: string | null;
}

export interface CatalogProduct {
  productId: number;
  name: string | null;
  code: string | null;
  description: string | null;
  brand: string | null;
  productSubCategory: string | null;
  productType: number;
  price: number | null;
  images: string[];
  availability: CatalogAvailability[];

  // ─── Offer / sale fields (InnoFlex catalog) ──────────────────────────────
  listPrice?: number | null;       // original crossed-out price
  discountPercent?: number | null; // derived discount %
  isOnSale?: boolean;
  offerLabel?: string | null;
  offerEndsAt?: string | null;     // ISO / UTC

  /** Desde cuándo tiene existencia publicada (ISO / UTC): cuándo llegó o se repuso. Null si ya tenía al empezar el seguimiento. */
  inStockSince?: string | null;
}

export interface CatalogPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  firstRow: number;
}

export interface CatalogEnvelope<T> {
  message: string;
  data: T;
}

// ─── Availability grouping helper ──────────────────────────────────────────────

export interface BranchAvailabilityGroup {
  branchId: number;
  branchName: string;
  warehouses: Availability[];
  totalQuantity: number;
  minPrice: number;
}

/**
 * Groups availability entries by branchId.
 * One branch can have multiple warehouses.
 */
export function groupAvailabilityByBranch(availability: Availability[]): BranchAvailabilityGroup[] {
  const map = new Map<number, BranchAvailabilityGroup>();
  for (const a of availability) {
    const id = a.branchId ?? 0;
    if (!map.has(id)) {
      map.set(id, {
        branchId: id,
        branchName: a.branchName,
        warehouses: [],
        totalQuantity: 0,
        minPrice: Infinity,
      });
    }
    const group = map.get(id)!;
    group.warehouses.push(a);
    group.totalQuantity += a.quantity;
    if (a.price > 0) group.minPrice = Math.min(group.minPrice, a.price);
  }
  return Array.from(map.values()).map((g) => ({
    ...g,
    minPrice: g.minPrice === Infinity ? 0 : g.minPrice,
  }));
}

// ─── InnoFlex API types ────────────────────────────────────────────────────────

export interface Availability {
  branchName: string;
  quantity: number;
  price: number;
  /** Extended fields from CatalogAvailability (optional — mapped at normalisation time) */
  stockId?: number;
  branchId?: number;
  warehouseId?: number | null;
  warehouseName?: string | null;
  stockLocation?: string | null;
  primaryIdentifier?: string | null;
}

/**
 * Product model as returned by InnoFlex catalog API.
 * `id` is a string alias for `productId` kept for cart/compare compatibility.
 *
 * Real InnoFlex fields:
 *   productId, name, images[], description, brand,
 *   productSubCategory, productSubCategoryId?, productType, price, availability[], code
 *
 * Fields NOT available in the real API (do not use):
 *   image (string), sku, featured, originalPrice, created_at, updated_at, active
 */
export interface Product {
  // InnoFlex native fields
  productId: number;
  name: string;
  /** Array of image URLs returned by InnoFlex. Never null — empty array when none. */
  images: string[];
  description: string | null;
  brand: string | null;
  productSubCategory: string | null;
  /** Numeric ID of the sub-category (used for API filters). Optional until backend returns it. */
  productSubCategoryId?: number;
  productType: number;   // 0=Producto, 1=Servicio, 2=Combo, 4=Suscripción
  price: number;         // effective price (already includes the offer when on sale)
  availability: Availability[];
  code?: string;         // Real InnoFlex product code (e.g. "CPU-R7-7700X")

  // ─── Offer / sale (from InnoFlex catalog) ────────────────────────────────
  /** Original crossed-out price. null when not on sale. */
  listPrice?: number | null;
  /** Derived discount percentage. null when not on sale. */
  discountPercent?: number | null;
  /** True when a live offer is applied to `price`. */
  isOnSale?: boolean;
  /** Optional offer badge label (e.g. "Black Friday"). */
  offerLabel?: string | null;
  /** Offer end datetime (ISO / UTC), for showing validity. */
  offerEndsAt?: string | null;

  /**
   * Desde cuándo tiene existencia publicada (ISO / UTC): la hora en que llegó o VOLVIÓ a tener
   * stock. Ordena «Novedades» (src/utils/latestProducts.ts). Null si ya tenía cuando EBM empezó a
   * llevar la cuenta, si es un servicio o si el API todavía no la manda.
   */
  inStockSince?: string | null;

  // Compatibility bridge (kept for CartContext / CompareContext / routing)
  id: string;            // = String(productId)
  /** @deprecated Prefer `code`. Kept as fallback for legacy links/components. */
  sku?: string;
  category: string;      // = productSubCategory ?? ''
  stock?: number;        // total stock across branches (WITH_STOCK types only)
}

/**
 * Product types that have real inventory (availability[]).
 * 0 = Stock | 2 = Consumible | 3 = Perecedero | 5 = Serie
 * Show "From $X" and "View Availability" button.
 */
export const WITH_STOCK_TYPES = [0, 2, 3, 5] as const;

/**
 * Product types that are services / fixed price (no availability).
 * 1 = Servicio | 4 = ServicioRecurrente | 6 = ServicioExterno
 * Show "$X" fixed price and "Add to cart".
 */
export const WITHOUT_STOCK_TYPES = [1, 4, 6] as const;

/** Returns true when this product type carries inventory */
export function hasStockType(productType: number): boolean {
  return (WITH_STOCK_TYPES as readonly number[]).includes(productType);
}

/**
 * True when the product has a *currently valid* offer ("oferta vigente"):
 *   - the catalog flags it as on sale,
 *   - the crossed-out listPrice is above the effective price, and
 *   - the offer has not expired (offerEndsAt is absent or still in the future).
 *
 * Use this — not the raw `isOnSale` flag — to decide whether to surface the
 * offers view, so expired promos never leak into the "current deals" list.
 */
export function isOfferActive(product: Product, now: Date = new Date()): boolean {
  if (!product.isOnSale) return false;
  if (product.listPrice == null || product.listPrice <= product.price) return false;
  if (product.offerEndsAt) {
    const end = new Date(product.offerEndsAt);
    if (!Number.isNaN(end.getTime()) && end.getTime() <= now.getTime()) return false;
  }
  return true;
}

export interface Category {
  id: string;
  label: string;
}

export type SortOption =
  | 'default'
  | 'name_asc'
  | 'name_desc'
  | 'price_asc'
  | 'price_desc'
  | 'brand_asc'
  | 'category_asc';

// ─── InnoFlex catalog query params ────────────────────────────────────────────

export interface CatalogFilter {
  column: 'brandId' | 'productSubCategoryId' | 'productType';
  value: string | number;
}

export interface CatalogSort {
  sortBy: 'name' | 'price' | 'brand' | 'productSubCategory';
  sortDirection: 'asc' | 'desc';
}

export interface CatalogFilters {
  page?: number;
  pageSize?: number;
  globalFilter?: string;
  allData?: boolean;
  filters?: CatalogFilter[];
  sorts?: CatalogSort[];
}

// ─── Legacy aliases kept for backward compatibility ─────────────────────────

/** @deprecated use CatalogFilters */
export type ProductFilters = CatalogFilters & {
  limit?: number;
  sort?: SortOption;
  category?: string;
  search?: string;
  in_stock?: boolean;
  min_price?: number;
  max_price?: number;
  featured?: boolean;
};

export interface PaginationMeta {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface PaginatedProducts {
  data: Product[];
  pagination: PaginationMeta;
}

// ─── Cart ─────────────────────────────────────────────────────────────────────

export interface CartItem extends Product {
  quantity: number;
  /** Sucursal donde se pide (src/config/branches.json). Sin valor = la principal. */
  branchKey?: string;
}
